import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Typography,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  Autocomplete,
  Checkbox,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Print as PrintIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CalendarToday,
  Business,
  DirectionsCar,
  Numbers,
  Layers,
  LocationOn,
  Person,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { MainSectionPanel, FormSectionPanel } from '../../../components/common';
import { useAuth } from '../../../auth';
import { ParteProduccionService } from './services/ParteProduccionService';
import { clienteService, type ClienteSearch } from './services/clienteService';
import { parteProduccionPdfService } from './services/parteProduccionPdfService';
import ProductoTipoBSelectionModal, { type DevolucionInternaInfo } from './components/ProductoTipoBSelectionModal';
import type {
  ParteFromGuiaInfo,
  GrupoMateriaPrima,
  GrupoProductoTerminado,
  MateriaPrimaParte,
  ProductoTerminadoParte,
  ProductoTerminadoTipoB,
  DERCInfoParte
} from './types';

type EstadoParte = 'PEN' | 'APR' | 'ANU';

const estadoLabels: Record<EstadoParte, string> = {
  PEN: 'Pendiente',
  APR: 'Aprobado',
  ANU: 'Anulado',
};

// Collapsible section for materias primas
const CollapsibleMateriaPrimaSection: React.FC<{
  grupo: GrupoMateriaPrima;
  defaultExpanded?: boolean;
}> = ({ grupo, defaultExpanded = true }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <Box sx={{ mb: 2 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          backgroundColor: '#f5f5f5',
          p: 1,
          borderRadius: 1,
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <IconButton size="small">
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
        <Typography variant="subtitle2" fontWeight={600}>
          {grupo.titulo}
        </Typography>
      </Box>
      <Collapse in={expanded}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#fafafa' }}>
                <TableCell sx={{ fontWeight: 600 }}>Código</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Artículo</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Peso Empaque</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Cantidad</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {grupo.items.map((item: MateriaPrimaParte) => (
                <TableRow key={item.id}>
                  <TableCell>
                    <Chip label={item.codigo} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>{item.articulo}</TableCell>
                  <TableCell align="center">{item.pesoEmpaqueInterior.toFixed(2)}</TableCell>
                  <TableCell align="center">
                    <Chip label={item.cantidad.toFixed(2)} size="small" color="primary" />
                  </TableCell>
                  <TableCell align="center">{item.total.toFixed(2)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ display: 'flex', gap: 2, mt: 1, p: 1, backgroundColor: '#e8f5e9', borderRadius: 1 }}>
          <Chip label={`Bolsas: ${grupo.totales.bolsas.toFixed(2)}`} size="small" color="success" variant="outlined" />
          <Chip label={`Saldos: ${grupo.totales.saldos.toFixed(2)}`} size="small" color="success" variant="outlined" />
          <Chip label={`Otros: ${grupo.totales.otros.toFixed(2)}`} size="small" color="success" variant="outlined" />
          <Chip label={`Peso: ${grupo.totales.peso.toFixed(2)}`} size="small" color="success" variant="outlined" />
        </Box>
      </Collapse>
    </Box>
  );
};

// Collapsible section for productos terminados
const CollapsibleProductoTerminadoSection: React.FC<{
  grupo: GrupoProductoTerminado;
  defaultExpanded?: boolean;
  onDevolucionInternaChange?: (itemId: number, checked: boolean) => void;
  onCantidadDevolucionInternaChange?: (itemId: number, cantidad: number) => void;
  readOnly?: boolean;
}> = ({ grupo, defaultExpanded = true, onDevolucionInternaChange, onCantidadDevolucionInternaChange, readOnly }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <Box sx={{ mb: 2 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          backgroundColor: '#e3f2fd',
          p: 1,
          borderRadius: 1,
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <IconButton size="small">
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
        <Typography variant="subtitle2" fontWeight={600}>
          {grupo.titulo}
        </Typography>
      </Box>
      <Collapse in={expanded}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#fafafa' }}>
                <TableCell sx={{ fontWeight: 600 }}>Código</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Artículo</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Peso Empaque</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Cantidad</TableCell>
                {!readOnly && (
                  <TableCell align="center" sx={{ fontWeight: 600, minWidth: 180 }}>Dev. Interna</TableCell>
                )}
                {!readOnly && (
                  <TableCell align="center" sx={{ fontWeight: 600 }}>Diferencia</TableCell>
                )}
                <TableCell align="center" sx={{ fontWeight: 600 }}>Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {grupo.items.map((item: ProductoTerminadoParte) => {
                const cantidadDevInt = item.devolucionInterna ? (item.cantidadDevolucionInterna || 0) : 0;
                const diferencia = item.cantidad - cantidadDevInt;
                const totalCalculado = diferencia * item.pesoEmpaqueInterior;
                return (
                  <TableRow key={item.id}>
                    <TableCell>
                      <Chip label={item.codigo} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>{item.articulo}</TableCell>
                    <TableCell align="center">{item.pesoEmpaqueInterior.toFixed(2)}</TableCell>
                    <TableCell align="center">
                      <Chip label={item.cantidad} size="small" color="default" variant="outlined" />
                    </TableCell>
                    {!readOnly && (
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                          <Checkbox
                            checked={item.devolucionInterna || false}
                            onChange={(e) => onDevolucionInternaChange?.(item.id, e.target.checked)}
                            size="small"
                          />
                          {item.devolucionInterna && (
                            <TextField
                              type="number"
                              size="small"
                              placeholder="Cant."
                              value={item.cantidadDevolucionInterna || ''}
                              onChange={(e) => onCantidadDevolucionInternaChange?.(item.id, parseFloat(e.target.value) || 0)}
                              sx={{ width: 70 }}
                              inputProps={{ min: 0, max: item.cantidad }}
                            />
                          )}
                        </Box>
                      </TableCell>
                    )}
                    {!readOnly && (
                      <TableCell align="center">
                        <Chip
                          label={diferencia}
                          size="small"
                          color={diferencia < item.cantidad ? "warning" : "primary"}
                        />
                      </TableCell>
                    )}
                    <TableCell align="center">{totalCalculado.toFixed(2)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        {(() => {
          // Calculate totals based on diferencia (cantidad - cantidadDevolucionInterna)
          const totalesDiferencia = grupo.items.reduce((acc, item) => {
            const cantidadDevInt = item.devolucionInterna ? (item.cantidadDevolucionInterna || 0) : 0;
            const diferencia = item.cantidad - cantidadDevInt;
            const totalItem = diferencia * item.pesoEmpaqueInterior;
            return {
              cajas: acc.cajas + diferencia,
              peso: acc.peso + totalItem
            };
          }, { cajas: 0, peso: 0 });

          return (
            <Box sx={{ display: 'flex', gap: 2, mt: 1, p: 1, backgroundColor: '#e3f2fd', borderRadius: 1 }}>
              <Chip label={`Cajas: ${totalesDiferencia.cajas.toFixed(2)}`} size="small" color="primary" variant="outlined" />
              <Chip label={`Bolsas: ${grupo.totales.bolsas.toFixed(2)}`} size="small" color="primary" variant="outlined" />
              <Chip label={`Saldos: ${grupo.totales.saldos.toFixed(2)}`} size="small" color="primary" variant="outlined" />
              <Chip label={`Otros: ${grupo.totales.otros.toFixed(2)}`} size="small" color="primary" variant="outlined" />
              <Chip label={`Peso: ${totalesDiferencia.peso.toFixed(2)}`} size="small" color="primary" variant="outlined" />
            </Box>
          );
        })()}
      </Collapse>
    </Box>
  );
};

const ParteProduccionFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const isEdit = id !== 'new';

  // Get guiaId from URL params (when coming from Guías de Producción)
  const guiaIdParam = searchParams.get('guiaId');

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);

  // Form state
  const [numeroComprobante, setNumeroComprobante] = useState<number>(0);
  const [fecha, setFecha] = useState<Date | null>(new Date());
  const [estado, setEstado] = useState<EstadoParte>('PEN');
  const [, setIdCliente] = useState<number | null>(null);
  const [selectedCliente, setSelectedCliente] = useState<ClienteSearch | null>(null);
  const [clienteOptions, setClienteOptions] = useState<ClienteSearch[]>([]);
  const [clienteLoading, setClienteLoading] = useState(false);
  const [placaVehiculo, setPlacaVehiculo] = useState<string>('');
  const [numeroGuiaRemision, setNumeroGuiaRemision] = useState<string>('');
  const [cajas, setCajas] = useState<number>(0);
  const [observaciones, setObservaciones] = useState<string>('');
  const [notas, setNotas] = useState<string>('');

  // DERC Info
  const [dercInfo, setDercInfo] = useState<ParteFromGuiaInfo['derc'] | null>(null);

  // Materias Primas and Productos Terminados
  const [materiasPrimas, setMateriasPrimas] = useState<GrupoMateriaPrima[]>([]);
  const [productosTerminados, setProductosTerminados] = useState<GrupoProductoTerminado[]>([]);

  // Productos Terminados Tipo B
  const [productosTipoB, setProductosTipoB] = useState<ProductoTerminadoTipoB[]>([]);
  const [tipoBDialogOpen, setTipoBDialogOpen] = useState(false);

  // Ref for Productos Tipo B section
  const productosTipoBSectionRef = useRef<HTMLDivElement>(null);

  // Load parte info from guía de producción (for new parte)
  const loadParteFromGuia = useCallback(async (guiaId: number) => {
    try {
      setLoading(true);
      setError(null);

      const parteInfo = await ParteProduccionService.getParteInfoFromGuia(guiaId);

      setDercInfo(parteInfo.derc);
      setMateriasPrimas(parteInfo.materiasPrimas);
      setProductosTerminados(parteInfo.productosTerminados);
      // La placa del vehículo del parte es independiente de la del DERC
      // (es el vehículo que lleva el producto terminado)
    } catch (err) {
      console.error('Error loading parte from guia:', err);
      setError('Error al cargar la información de la guía de producción');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load existing parte for edit/view
  const loadParteForEdit = useCallback(async (parteId: number) => {
    try {
      setLoading(true);
      setError(null);

      const parteDetail = await ParteProduccionService.getById(parteId);

      setNumeroComprobante(parteDetail.numeroComprobante);
      setFecha(new Date(parteDetail.fecha));
      setIdCliente(parteDetail.idCliente);
      setPlacaVehiculo(parteDetail.placaVehiculo || '');
      setNumeroGuiaRemision(parteDetail.numeroGuiaRemision || '');
      setCajas(parteDetail.cajas);
      setEstado(parteDetail.estado as EstadoParte || 'PEN');
      setObservaciones(parteDetail.observaciones || '');
      setNotas(parteDetail.notas || '');
      setDercInfo(parteDetail.derc);
      setMateriasPrimas(parteDetail.materiasPrimas);
      setProductosTerminados(parteDetail.productosTerminados);
      setProductosTipoB(parteDetail.productosTipoB || []);

      // Set cliente info
      if (parteDetail.clienteRazonSocial) {
        setSelectedCliente({
          id: parteDetail.idCliente,
          codigo: parteDetail.clienteCodigo || '',
          razonSocial: parteDetail.clienteRazonSocial,
          nombreFantasia: undefined,
          nroIdentificacion: undefined
        });
      }
    } catch (err) {
      console.error('Error loading parte for edit:', err);
      setError('Error al cargar el parte de producción');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (guiaIdParam && !isEdit) {
      loadParteFromGuia(parseInt(guiaIdParam));
    } else if (isEdit && id) {
      loadParteForEdit(parseInt(id));
    } else {
      setLoading(false);
    }
  }, [guiaIdParam, isEdit, id, loadParteFromGuia, loadParteForEdit]);

  const handleVolver = () => {
    navigate('/produccion/partes-produccion');
  };

  const handleEstadoChange = async (event: SelectChangeEvent) => {
    const nuevoEstado = event.target.value as EstadoParte;
    if (isEdit && id) {
      // Si estamos editando, actualizamos el estado en el backend
      try {
        setLoading(true);
        await ParteProduccionService.updateEstado(parseInt(id), nuevoEstado);
        setEstado(nuevoEstado);
        setAlertMessage(`Estado actualizado a ${estadoLabels[nuevoEstado]}`);

        // Redirigir al listado después de cambiar el estado
        setTimeout(() => {
          navigate('/produccion/partes-produccion');
        }, 1500);
      } catch (err) {
        console.error('Error updating estado:', err);
        setError('Error al actualizar el estado');
        setLoading(false);
      }
    } else {
      // Si es nuevo, solo actualizamos el estado local
      setEstado(nuevoEstado);
    }
  };

  const handleClienteInputChange = async (_event: React.SyntheticEvent, value: string) => {
    if (!value || value.length < 2) {
      setClienteOptions([]);
      return;
    }

    setClienteLoading(true);
    try {
      const results = await clienteService.search(value);
      setClienteOptions(results);
    } catch (error) {
      console.error('Error searching clientes:', error);
      setClienteOptions([]);
    } finally {
      setClienteLoading(false);
    }
  };

  const handleClienteChange = (_event: React.SyntheticEvent, value: ClienteSearch | null) => {
    setSelectedCliente(value);
    setIdCliente(value?.id || null);
  };

  const handleDevolucionInternaChange = (itemId: number, checked: boolean) => {
    setProductosTerminados(prev =>
      prev.map(grupo => ({
        ...grupo,
        items: grupo.items.map(item =>
          item.id === itemId
            ? { ...item, devolucionInterna: checked, cantidadDevolucionInterna: checked ? item.cantidad : 0 }
            : item
        ),
      }))
    );
  };

  const handleCantidadDevolucionInternaChange = (itemId: number, cantidad: number) => {
    setProductosTerminados(prev =>
      prev.map(grupo => ({
        ...grupo,
        items: grupo.items.map(item => {
          if (item.id !== itemId) return item;
          // No permitir devolver más de lo que hay en cantidad
          const cantidadValidada = Math.min(Math.max(0, cantidad), item.cantidad);
          return { ...item, cantidadDevolucionInterna: cantidadValidada };
        }),
      }))
    );
  };

  // Productos Terminados Tipo B handlers
  const handleOpenTipoBDialog = () => {
    setTipoBDialogOpen(true);
  };

  const handleCloseTipoBDialog = () => {
    setTipoBDialogOpen(false);
  };

  const handleAddProductosTipoB = (selectedProducts: ProductoTerminadoTipoB[]) => {
    // Filter out duplicates - products already added
    const newProducts = selectedProducts.filter(
      sp => !productosTipoB.some(existing => existing.idGuiaProduccion === sp.idGuiaProduccion)
    );
    if (newProducts.length === 0) {
      setAlertMessage('Los productos seleccionados ya están agregados.');
      setTimeout(() => setAlertMessage(null), 3000);
      return;
    }
    if (newProducts.length < selectedProducts.length) {
      const duplicateCount = selectedProducts.length - newProducts.length;
      setAlertMessage(`${newProducts.length} productos agregados. ${duplicateCount} ya estaban en la lista.`);
      setTimeout(() => setAlertMessage(null), 4000);
    }
    setProductosTipoB(prev => [...prev, ...newProducts]);

    // Scroll to Productos Tipo B section after adding products
    setTimeout(() => {
      productosTipoBSectionRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, 100);
  };

  const handleRemoveProductoTipoB = (id: number) => {
    setProductosTipoB(prev => prev.filter(item => item.id !== id));
  };

  const handlePrint = () => {
    if (!isEdit || estado !== 'APR' || !dercInfo) {
      setError('Solo se puede imprimir cuando el parte está Aprobado');
      return;
    }

    try {
      // Construir el objeto de datos completo para el PDF
      const pdfData = {
        id: parseInt(id!),
        numeroComprobante,
        fecha: fecha?.toISOString() || '',
        idCliente: selectedCliente?.id || 0,
        clienteCodigo: selectedCliente?.codigo,
        clienteRazonSocial: selectedCliente?.razonSocial,
        placaVehiculo,
        numeroGuiaRemision,
        cajas,
        estado,
        observaciones,
        notas,
        derc: dercInfo as DERCInfoParte,
        materiasPrimas,
        productosTerminados,
        productosTipoB
      };

      parteProduccionPdfService.generatePdf(pdfData);
      setAlertMessage('PDF generado exitosamente');
    } catch (err) {
      console.error('Error generating PDF:', err);
      setError('Error al generar el PDF');
    }
  };

  const handleSubmit = async () => {
    if (!selectedCliente) {
      setError('Debe seleccionar un cliente');
      return;
    }

    if (!fecha) {
      setError('Debe seleccionar una fecha');
      return;
    }

    if (!placaVehiculo || placaVehiculo.trim() === '') {
      setError('Debe ingresar la placa del vehículo');
      return;
    }

    if (!numeroGuiaRemision || numeroGuiaRemision.trim() === '') {
      setError('Debe ingresar el número de guía de remisión');
      return;
    }

    if (!cajas || cajas <= 0) {
      setError('Debe ingresar la cantidad de cajas (mayor a 0)');
      return;
    }

    setError(null);
    setLoading(true);

    try {
      if (isEdit && id) {
        // Update existing parte
        // Recopilar productos tipo A con sus devoluciones internas
        const productosTipoAUpdate = productosTerminados.flatMap(grupo =>
          grupo.items.map(producto => ({
            idGuiaProduccionDetalle: producto.id,
            cantidad: producto.cantidad,
            devolucionInterna: producto.devolucionInterna || false,
            cantidadDevolucionInterna: producto.devolucionInterna
              ? (producto.cantidadDevolucionInterna || 0)
              : undefined
          }))
        );

        await ParteProduccionService.update(parseInt(id), {
          idCliente: selectedCliente.id,
          placaVehiculo: placaVehiculo || undefined,
          numeroGuiaRemision: numeroGuiaRemision || undefined,
          fecha: fecha.toISOString(),
          cajas: cajas,
          observaciones: observaciones || undefined,
          notas: notas || undefined,
          productosTipoA: productosTipoAUpdate,
          productosTipoB: productosTipoB.map(p => ({
            idGuiaProduccionDetalle: p.id,
            cantidad: p.cantidad
          }))
        });
        setAlertMessage('Parte de producción actualizado exitosamente');
      } else {
        // Create new parte
        if (!guiaIdParam) {
          setError('Error: No se encontró la guía de producción');
          setLoading(false);
          return;
        }

        // Recopilar todos los productos tipo A de todas las guías
        // El primer grupo corresponde a la guía principal, los demás son adicionales
        // La cantidad enviada es la diferencia (cantidad original - devolución interna)
        const todosProductosTipoA = productosTerminados.flatMap(grupo =>
          grupo.items.map(producto => {
            const cantidadDevInt = producto.devolucionInterna ? (producto.cantidadDevolucionInterna || 0) : 0;
            const cantidadFinal = producto.cantidad - cantidadDevInt;
            return {
              idGuiaProduccionDetalle: producto.id,
              cantidad: cantidadFinal,
              devolucionInterna: producto.devolucionInterna || false,
              cantidadDevolucionInterna: cantidadDevInt > 0 ? cantidadDevInt : undefined
            };
          })
        );

        const createRequest = {
          idGuiaProduccion: parseInt(guiaIdParam),
          idCliente: selectedCliente.id,
          placaVehiculo: placaVehiculo || undefined,
          numeroGuiaRemision: numeroGuiaRemision || undefined,
          fecha: fecha.toISOString(),
          cajas: cajas,
          observaciones: observaciones || undefined,
          notas: notas || undefined,
          productosTipoA: todosProductosTipoA,
          productosTipoB: productosTipoB.map(p => ({
            idGuiaProduccionDetalle: p.id,
            cantidad: p.cantidad
          }))
        };
        console.log('CREATE PARTE REQUEST:', JSON.stringify(createRequest, null, 2));
        console.log('productosTipoB state:', productosTipoB);
        await ParteProduccionService.create(createRequest);
        setAlertMessage('Parte de producción creado exitosamente');
      }

      setTimeout(() => {
        navigate('/produccion/partes-produccion');
      }, 1000);
    } catch (err: unknown) {
      console.error('Error saving parte:', err);
      // Extract error message from error object
      let errorMessage = 'Error al guardar el parte de producción';
      if (err && typeof err === 'object') {
        const errorObj = err as Record<string, unknown>;

        // First try userMessage (set by errorService with server message)
        if (typeof errorObj.userMessage === 'string' && errorObj.userMessage) {
          errorMessage = errorObj.userMessage;
        }
        // Try serverMessage from context
        else if (errorObj.context && typeof errorObj.context === 'object') {
          const ctx = errorObj.context as Record<string, unknown>;
          if (typeof ctx.serverMessage === 'string' && ctx.serverMessage) {
            errorMessage = ctx.serverMessage;
          }
        }
        // Try to get message from originalError (from errorService)
        else if (errorObj.originalError && typeof errorObj.originalError === 'object') {
          const origErr = errorObj.originalError as { response?: { data?: unknown } };
          if (origErr.response?.data && typeof origErr.response.data === 'string') {
            errorMessage = origErr.response.data;
          }
        }
        // Try to get message from direct axios response
        else if ('response' in errorObj) {
          const resp = (errorObj as { response?: { data?: unknown } }).response;
          if (resp?.data) {
            if (typeof resp.data === 'string') {
              errorMessage = resp.data;
            } else if (typeof resp.data === 'object' && resp.data && 'message' in resp.data) {
              errorMessage = String((resp.data as { message: unknown }).message);
            }
          }
        }
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('es-ES');
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <MainSectionPanel>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      </MainSectionPanel>
    );
  }

  return (
      <MainSectionPanel>
        <Paper sx={{ p: 3 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={handleVolver}
              sx={{ mr: 2 }}
            >
              Volver
            </Button>
            <Typography variant="h5" component="h1" fontWeight={600} sx={{ flex: 1 }}>
              {isEdit ? `Parte de Producción N° ${numeroComprobante}` : 'Nuevo Parte de Producción'}
            </Typography>
            {isEdit && estado === 'APR' && (
              <Button
                variant="outlined"
                color="info"
                startIcon={<PrintIcon />}
                onClick={handlePrint}
              >
                Imprimir PDF
              </Button>
            )}
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {alertMessage && (
            <Alert severity="success" sx={{ mb: 3 }} onClose={() => setAlertMessage(null)}>
              {alertMessage}
            </Alert>
          )}

          {/* Estado y Tipo */}
          <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <Chip
              label="PRODUCCIÓN"
              color="info"
              size="medium"
              variant="filled"
              sx={{ fontWeight: 600 }}
            />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>Estado:</Typography>
              <Select
                value={estado}
                onChange={handleEstadoChange}
                size="small"
                sx={{ minWidth: 150 }}
              >
                <MenuItem value="PEN">
                  <Chip label="Pendiente" color="warning" size="small" />
                </MenuItem>
                <MenuItem value="APR">
                  <Chip label="Aprobado" color="success" size="small" />
                </MenuItem>
                <MenuItem value="ANU">
                  <Chip label="Anulado" color="error" size="small" />
                </MenuItem>
              </Select>
            </Box>
          </Box>

          {/* Información del DERC */}
          {dercInfo && (
            <FormSectionPanel title="Información del DERC">
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Numbers fontSize="small" color="primary" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        N° DERC
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {dercInfo.numeroDocumento || 'N/A'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarToday fontSize="small" color="primary" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Fecha de Extracción
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {formatDate(dercInfo.fechaExtraccion as unknown as string)}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Business fontSize="small" color="primary" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Asociación
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {dercInfo.asociacion || 'N/A'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Numbers fontSize="small" color="primary" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        N° Mallas
                      </Typography>
                      <Chip
                        label={dercInfo.numeroMallas}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </Box>
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Layers fontSize="small" color="primary" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        N° Manojos
                      </Typography>
                      <Chip
                        label={dercInfo.numeroManojos || 0}
                        size="small"
                        color="secondary"
                        variant="outlined"
                      />
                    </Box>
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocationOn fontSize="small" color="primary" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Procedencia
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {dercInfo.procedencia || 'N/A'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DirectionsCar fontSize="small" color="primary" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Placa de Vehículo
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {dercInfo.placaVehiculo || 'N/A'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </FormSectionPanel>
          )}

          {/* Información de Salida del Producto */}
          <FormSectionPanel title="Información de Salida del Producto">
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField
                  fullWidth
                  label="N° Parte"
                  value={numeroComprobante || 'Nuevo'}
                  disabled
                  size="small"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <DatePicker
                  label="Fecha"
                  value={fecha}
                  onChange={(newValue) => setFecha(newValue)}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField
                  fullWidth
                  required
                  label="Placa de Vehículo"
                  value={placaVehiculo}
                  onChange={(e) => setPlacaVehiculo(e.target.value)}
                  size="small"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField
                  fullWidth
                  required
                  label="N° Guía Remisión"
                  value={numeroGuiaRemision}
                  onChange={(e) => setNumeroGuiaRemision(e.target.value)}
                  size="small"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField
                  fullWidth
                  required
                  label="Cantidad de Cajas"
                  type="number"
                  value={cajas}
                  onChange={(e) => setCajas(parseFloat(e.target.value) || 0)}
                  size="small"
                  inputProps={{ min: 1 }}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Person fontSize="small" color="primary" />
                  <Typography variant="body2" sx={{ fontWeight: 500, minWidth: 60 }}>Cliente:</Typography>
                  <TextField
                    size="small"
                    value={selectedCliente?.codigo || ''}
                    sx={{ width: 100 }}
                    disabled
                  />
                  <Autocomplete
                    sx={{ flex: 1 }}
                    options={clienteOptions}
                    value={selectedCliente}
                    onChange={handleClienteChange}
                    onInputChange={handleClienteInputChange}
                    getOptionLabel={(option) => `${option.razonSocial}${option.nombreFantasia ? ` (${option.nombreFantasia})` : ''}`}
                    loading={clienteLoading}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    noOptionsText="Escriba para buscar clientes..."
                    loadingText="Buscando..."
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Buscar cliente..."
                        size="small"
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        <Box>
                          <Typography variant="body2">{option.razonSocial}</Typography>
                          {option.nombreFantasia && (
                            <Typography variant="caption" color="text.secondary">
                              {option.nombreFantasia}
                            </Typography>
                          )}
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {option.codigo}
                          </Typography>
                        </Box>
                      </li>
                    )}
                  />
                </Box>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Responsable"
                  value={user?.name || ''}
                  disabled
                  size="small"
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Observaciones"
                  value={observaciones}
                  onChange={(e) => setObservaciones(e.target.value)}
                  placeholder="Observaciones adicionales"
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Notas"
                  value={notas}
                  onChange={(e) => setNotas(e.target.value)}
                  placeholder="Notas adicionales"
                  multiline
                  rows={2}
                />
              </Grid>
            </Grid>
          </FormSectionPanel>

          {/* Materias Primas Section */}
          <FormSectionPanel title="Materias Primas">
            {materiasPrimas.length > 0 ? (
              <>
                {materiasPrimas.map((grupo, index) => (
                  <CollapsibleMateriaPrimaSection
                    key={index}
                    grupo={grupo}
                    defaultExpanded={materiasPrimas.length <= 1}
                  />
                ))}
                {/* Total de todos los grupos de Materias Primas */}
                {materiasPrimas.length > 1 && (
                  <Box sx={{ display: 'flex', gap: 2, mt: 2, p: 1.5, backgroundColor: '#388e3c', borderRadius: 1 }}>
                    <Typography variant="subtitle2" sx={{ color: 'white', fontWeight: 600, mr: 2 }}>
                      TOTAL MATERIAS PRIMAS:
                    </Typography>
                    <Chip
                      label={`Bolsas: ${materiasPrimas.reduce((sum, g) => sum + g.totales.bolsas, 0).toFixed(2)}`}
                      size="small"
                      sx={{ backgroundColor: 'white', color: '#388e3c', fontWeight: 600 }}
                    />
                    <Chip
                      label={`Saldos: ${materiasPrimas.reduce((sum, g) => sum + g.totales.saldos, 0).toFixed(2)}`}
                      size="small"
                      sx={{ backgroundColor: 'white', color: '#388e3c', fontWeight: 600 }}
                    />
                    <Chip
                      label={`Otros: ${materiasPrimas.reduce((sum, g) => sum + g.totales.otros, 0).toFixed(2)}`}
                      size="small"
                      sx={{ backgroundColor: 'white', color: '#388e3c', fontWeight: 600 }}
                    />
                    <Chip
                      label={`Peso: ${materiasPrimas.reduce((sum, g) => sum + g.totales.peso, 0).toFixed(2)}`}
                      size="small"
                      sx={{ backgroundColor: 'white', color: '#388e3c', fontWeight: 600 }}
                    />
                  </Box>
                )}
              </>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                No hay materias primas asociadas
              </Typography>
            )}
          </FormSectionPanel>

          {/* Productos Terminados Section */}
          <FormSectionPanel title="Productos Terminados">
            {productosTerminados.length > 0 ? (
              <>
                {productosTerminados.map((grupo, index) => (
                  <CollapsibleProductoTerminadoSection
                    key={index}
                    grupo={grupo}
                    defaultExpanded={productosTerminados.length <= 1}
                    onDevolucionInternaChange={handleDevolucionInternaChange}
                    onCantidadDevolucionInternaChange={handleCantidadDevolucionInternaChange}
                  />
                ))}
                {/* Total de todos los grupos de Productos Terminados A */}
                {productosTerminados.length > 1 && (() => {
                  // Calcular totales basados en diferencia (cantidad - cantidadDevolucionInterna)
                  const totalesGenerales = productosTerminados.reduce((acc, grupo) => {
                    const totalesGrupo = grupo.items.reduce((itemAcc, item) => {
                      const cantidadDevInt = item.devolucionInterna ? (item.cantidadDevolucionInterna || 0) : 0;
                      const diferencia = item.cantidad - cantidadDevInt;
                      const totalItem = diferencia * item.pesoEmpaqueInterior;
                      return {
                        cajas: itemAcc.cajas + diferencia,
                        peso: itemAcc.peso + totalItem
                      };
                    }, { cajas: 0, peso: 0 });
                    return {
                      cajas: acc.cajas + totalesGrupo.cajas,
                      bolsas: acc.bolsas + grupo.totales.bolsas,
                      saldos: acc.saldos + grupo.totales.saldos,
                      otros: acc.otros + grupo.totales.otros,
                      peso: acc.peso + totalesGrupo.peso
                    };
                  }, { cajas: 0, bolsas: 0, saldos: 0, otros: 0, peso: 0 });

                  return (
                    <Box sx={{ display: 'flex', gap: 2, mt: 2, p: 1.5, backgroundColor: '#1976d2', borderRadius: 1 }}>
                      <Typography variant="subtitle2" sx={{ color: 'white', fontWeight: 600, mr: 2 }}>
                        TOTAL PRODUCTOS A:
                      </Typography>
                      <Chip
                        label={`Cajas: ${totalesGenerales.cajas.toFixed(2)}`}
                        size="small"
                        sx={{ backgroundColor: 'white', color: '#1976d2', fontWeight: 600 }}
                      />
                      <Chip
                        label={`Bolsas: ${totalesGenerales.bolsas.toFixed(2)}`}
                        size="small"
                        sx={{ backgroundColor: 'white', color: '#1976d2', fontWeight: 600 }}
                      />
                      <Chip
                        label={`Saldos: ${totalesGenerales.saldos.toFixed(2)}`}
                        size="small"
                        sx={{ backgroundColor: 'white', color: '#1976d2', fontWeight: 600 }}
                      />
                      <Chip
                        label={`Otros: ${totalesGenerales.otros.toFixed(2)}`}
                        size="small"
                        sx={{ backgroundColor: 'white', color: '#1976d2', fontWeight: 600 }}
                      />
                      <Chip
                        label={`Peso: ${totalesGenerales.peso.toFixed(2)}`}
                        size="small"
                        sx={{ backgroundColor: 'white', color: '#1976d2', fontWeight: 600 }}
                      />
                    </Box>
                  );
                })()}
              </>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                No hay productos terminados asociados
              </Typography>
            )}
          </FormSectionPanel>

          {/* Productos B y Devolución Interna Section */}
          <div ref={productosTipoBSectionRef}>
            <FormSectionPanel
              title="Productos B y Devolución Interna"
              collapsible={true}
              defaultExpanded={true}
            >
            <Box sx={{ mb: 2 }}>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<AddIcon />}
                onClick={handleOpenTipoBDialog}
                size="small"
              >
                Agregar
              </Button>
            </Box>
            {productosTipoB.length > 0 ? (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#fff3e0' }}>
                      <TableCell sx={{ fontWeight: 600, width: 50 }}>Acc.</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Código</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Artículo</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600 }}>Peso Empaque</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600 }}>Cantidad</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600 }}>Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {productosTipoB.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRemoveProductoTipoB(item.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          <Chip label={item.codigo} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>{item.producto}</TableCell>
                        <TableCell align="center">{item.peso.toFixed(2)}</TableCell>
                        <TableCell align="center">
                          <Chip label={item.cantidad.toFixed(2)} size="small" color="secondary" />
                        </TableCell>
                        <TableCell align="center">{item.total.toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                No hay productos B ni devoluciones internas. Click en "Agregar" para buscar.
              </Typography>
            )}
            {productosTipoB.length > 0 && (
              <Box sx={{ display: 'flex', gap: 2, mt: 1, p: 1, backgroundColor: '#fff3e0', borderRadius: 1 }}>
                <Chip
                  label={`Cantidad Total: ${productosTipoB.reduce((sum, item) => sum + item.cantidad, 0).toFixed(2)}`}
                  size="small"
                  color="warning"
                  variant="outlined"
                />
                <Chip
                  label={`Peso Total: ${productosTipoB.reduce((sum, item) => sum + item.total, 0).toFixed(2)}`}
                  size="small"
                  color="warning"
                  variant="outlined"
                />
              </Box>
            )}
          </FormSectionPanel>
          </div>

          {/* TOTAL GENERAL: Productos A + Productos B */}
          {(productosTerminados.length > 0 || productosTipoB.length > 0) && (() => {
            // Calcular pesoTotalA basado en diferencia (cantidad - cantidadDevolucionInterna)
            const pesoTotalA = productosTerminados.reduce((sum, grupo) => {
              return sum + grupo.items.reduce((itemSum, item) => {
                const cantidadDevInt = item.devolucionInterna ? (item.cantidadDevolucionInterna || 0) : 0;
                const diferencia = item.cantidad - cantidadDevInt;
                return itemSum + (diferencia * item.pesoEmpaqueInterior);
              }, 0);
            }, 0);
            const pesoTotalB = productosTipoB.reduce((sum, item) => sum + item.total, 0);
            const pesoTotalProductos = pesoTotalA + pesoTotalB;
            const pesoTotalMateriasPrimas = materiasPrimas.reduce((sum, g) => sum + g.totales.peso, 0);
            const rendimiento = pesoTotalMateriasPrimas > 0
              ? pesoTotalProductos / pesoTotalMateriasPrimas
              : 0;

            return (
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                mt: 2,
                p: 2,
                backgroundColor: '#2e7d32',
                borderRadius: 1
              }}>
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, textAlign: 'center' }}>
                  TOTAL GENERAL (PRODUCTOS A + B)
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, flexWrap: 'wrap' }}>
                  <Chip
                    label={`Peso Total A: ${pesoTotalA.toFixed(2)} kg`}
                    size="medium"
                    sx={{ backgroundColor: '#1976d2', color: 'white', fontWeight: 600, fontSize: '0.9rem' }}
                  />
                  <Chip
                    label={`Peso Total B: ${pesoTotalB.toFixed(2)} kg`}
                    size="medium"
                    sx={{ backgroundColor: '#ed6c02', color: 'white', fontWeight: 600, fontSize: '0.9rem' }}
                  />
                  <Chip
                    label={`PESO TOTAL: ${pesoTotalProductos.toFixed(2)} kg`}
                    size="medium"
                    sx={{ backgroundColor: 'white', color: '#2e7d32', fontWeight: 700, fontSize: '1rem' }}
                  />
                  <Chip
                    label={`RENDIMIENTO: ${rendimiento.toFixed(2)}`}
                    size="medium"
                    sx={{ backgroundColor: '#ffeb3b', color: '#000', fontWeight: 700, fontSize: '1rem' }}
                  />
                </Box>
              </Box>
            );
          })()}

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
            <Button
              variant="outlined"
              onClick={handleVolver}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? 'Guardando...' : 'Guardar'}
            </Button>
          </Box>

          {/* Modal para seleccionar Productos Terminados Tipo B */}
          <ProductoTipoBSelectionModal
            open={tipoBDialogOpen}
            onClose={handleCloseTipoBDialog}
            onSelectProducts={handleAddProductosTipoB}
            parteId={isEdit && id ? parseInt(id) : undefined}
            devolucionesInternas={
              productosTerminados.flatMap(grupo =>
                grupo.items
                  .filter(item => item.devolucionInterna && item.cantidadDevolucionInterna && item.cantidadDevolucionInterna > 0)
                  .map(item => ({
                    idGuiaProduccionDetalle: item.id,
                    cantidadDevolucionInterna: item.cantidadDevolucionInterna || 0,
                    pesoEmpaqueInterior: item.pesoEmpaqueInterior,
                  } as DevolucionInternaInfo))
              )
            }
          />

        </Paper>
      </MainSectionPanel>
  );
};

export default ParteProduccionFormPage;
